import type { GeometryType, LayerConfig, LegendPaint, TreeNode } from '../../api/types';
import { swatchShape } from '../legend';
import { groupKey, isGroup } from '../tree';

/**
 * The Legend tab's model, ported from Layers Pro's custom legend (parseLegend / dedupLegend / renderFlat): a real map
 * legend rather than a second layer list. Built from each layer's `legend` entries instead of a renderer.
 */

export interface LegendSymbol {
  geometry: GeometryType;
  paint: LegendPaint;
}

export interface LegendRowModel {
  label: string;
  symbol: LegendSymbol;
}

export interface LegendLeaf {
  isGroup: false;
  key: string;
  title: string;
  rows: LegendRowModel[];
  /** The layer this leaf came from; absent on a group collapsed to one inline row. */
  layerId?: string;
}

export interface LegendGroup {
  isGroup: true;
  key: string;
  title: string;
  children: LegendNode[];
}

export type LegendNode = LegendLeaf | LegendGroup;

/**
 * Walk the theme tree keeping what is drawn: a layer is listed when `shown(id)` says so, and a group when anything
 * under it is. Leaves with no legend entries are dropped, as the production parse drops layers with no symbols.
 */
export function buildLegend(
  tree: readonly TreeNode[],
  layers: ReadonlyMap<string, Pick<LayerConfig, 'title' | 'geometry' | 'legend'>>,
  shown: (layerId: string) => boolean,
  groupOn: (key: string) => boolean = () => true,
): LegendNode[] {
  const walk = (n: TreeNode): LegendNode | null => {
    if (isGroup(n)) {
      const key = groupKey(n.group);
      if (!groupOn(key)) return null;
      const children = n.children.map(walk).filter((c): c is LegendNode => c !== null);
      return children.length ? { isGroup: true, key, title: n.title, children } : null;
    }
    const layer = layers.get(n.layer);
    if (!layer || !shown(n.layer)) return null;
    const rows = layer.legend.map((e) => ({
      label: e.label ?? '',
      symbol: { geometry: layer.geometry.type, paint: e.paint },
    }));
    if (!rows.length) return null;
    return { isGroup: false, key: `${n.layer}|${layer.title}`, title: layer.title, rows, layerId: n.layer };
  };
  return tree.map(walk).filter((c): c is LegendNode => c !== null);
}

/** A stable signature for a symbol: its drawn shape and paint, keys sorted. */
export function symbolSignature(s: LegendSymbol): string {
  const paint = s.paint as Record<string, unknown>;
  const sorted = Object.keys(paint)
    .sort()
    .map((k) => [k, paint[k]]);
  return JSON.stringify([swatchShape(s.geometry), sorted]);
}

const leafKey = (n: LegendLeaf): string =>
  n.rows.length === 1
    ? symbolSignature(n.rows[0]!.symbol)
    : n.rows.map((r) => `${symbolSignature(r.symbol)}~${r.label}`).join(';');

const groupSig = (n: LegendGroup): string =>
  '{' +
  n.children
    .map((c) => (c.isGroup ? groupSig(c) : leafKey(c)))
    .slice()
    .sort()
    .join(',') +
  '}';

/**
 * Combine identical-symbol entries: sibling single-symbol layers that share a symbol collapse to one; a group that
 * reduces to a single single-symbol leaf becomes one inline row titled by the group; and structurally identical
 * sibling groups merge, so each symbol shows once. A single-symbol leaf merged from differently labelled sources is
 * relabelled with its parent group's name.
 */
export function dedupLegend(nodes: readonly LegendNode[]): LegendNode[] {
  const dedup = (list: readonly LegendNode[], parentTitle: string): LegendNode[] => {
    const proc: LegendNode[] = list.map((n) => {
      if (!n.isGroup) return n;
      const kids = dedup(n.children, n.title);
      const only = kids[0];
      if (kids.length === 1 && only && !only.isGroup && only.rows.length === 1) {
        return { isGroup: false, key: n.key, title: n.title, rows: [{ label: n.title, symbol: only.rows[0]!.symbol }] };
      }
      return { ...n, children: kids };
    });
    const gCount = new Map<string, number>();
    for (const n of proc) if (n.isGroup) gCount.set(groupSig(n), (gCount.get(groupSig(n)) ?? 0) + 1);
    const out: LegendNode[] = [];
    const handledGroup = new Set<string>();
    const leafSeen = new Map<string, { idx: number; labels: Set<string> }>();
    const emitLeaf = (n: LegendLeaf): void => {
      const k = leafKey(n);
      const label = n.rows[0]?.label ?? '';
      const seen = leafSeen.get(k);
      if (seen) {
        seen.labels.add(label);
        return;
      }
      leafSeen.set(k, { idx: out.length, labels: new Set([label]) });
      out.push(n);
    };
    for (const n of proc) {
      if (n.isGroup) {
        const k = groupSig(n);
        if (handledGroup.has(k)) continue;
        handledGroup.add(k);
        // Identical siblings: pull the content up and drop the differing names.
        if ((gCount.get(k) ?? 0) > 1) {
          for (const c of n.children) {
            if (c.isGroup) out.push(c);
            else emitLeaf(c);
          }
        } else out.push(n);
      } else emitLeaf(n);
    }
    for (const e of leafSeen.values()) {
      const node = out[e.idx];
      if (node && !node.isGroup && node.rows.length === 1 && e.labels.size > 1) {
        out[e.idx] = { ...node, rows: [{ label: parentTitle || node.rows[0]!.label, symbol: node.rows[0]!.symbol }] };
      }
    }
    return out;
  };
  return dedup(nodes, '');
}

/** Groups off: a flat legend, each distinct leaf once (single-symbol layers by symbol, others by their symbol set). */
export function flatLeaves(nodes: readonly LegendNode[]): LegendLeaf[] {
  const seen = new Set<string>();
  const leaves: LegendLeaf[] = [];
  const walk = (n: LegendNode): void => {
    if (n.isGroup) {
      n.children.forEach(walk);
      return;
    }
    if (!n.rows.length) return;
    const k = leafKey(n);
    if (seen.has(k)) return;
    seen.add(k);
    leaves.push(n);
  };
  nodes.forEach(walk);
  return leaves;
}

/** Keep only the leaves `keep` accepts, then the groups left with anything in them ("Visible features only"). */
export function pruneLegend(nodes: readonly LegendNode[], keep: (layerId: string) => boolean): LegendNode[] {
  const prune = (list: readonly LegendNode[]): LegendNode[] =>
    list.flatMap((n): LegendNode[] => {
      if (n.isGroup) {
        const children = prune(n.children);
        return children.length ? [{ ...n, children }] : [];
      }
      return n.layerId === undefined || keep(n.layerId) ? [n] : [];
    });
  return prune(nodes);
}

export interface LegendPrefs {
  titles: boolean;
  groups: boolean;
  size: number;
  inView: boolean;
}

export const DEFAULT_LEGEND_PREFS: LegendPrefs = { titles: false, groups: false, size: 14, inView: false };

/** Parse stored legend preferences; anything malformed falls back to the defaults. */
export function parseLegendPrefs(raw: string | null): LegendPrefs {
  try {
    const x = JSON.parse(raw ?? '{}') as Record<string, unknown>;
    const size = Number(x['size']);
    return {
      titles: x['titles'] === true,
      groups: x['groups'] === true,
      size: Number.isFinite(size) && size >= 9 && size <= 28 ? size : DEFAULT_LEGEND_PREFS.size,
      inView: x['inView'] === true,
    };
  } catch {
    return { ...DEFAULT_LEGEND_PREFS };
  }
}
